<!--
Copyright (C) 2026 Checkmk GmbH - License: Checkmk Enterprise License
This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
conditions defined in the file COPYING, which is part of this source code package.
-->

<script setup lang="ts">
import { nextTick, onMounted, ref, useTemplateRef, watch } from 'vue'

import usei18n from '@/lib/i18n'

import CmkButton from '@/components/CmkButton'
import CmkIcon from '@/components/CmkIcon'
import CmkPopupDialog from '@/components/CmkPopupDialog.vue'
import { formatDateOrRelativeDay } from '@/components/date-time/formatDateOrRelativeDay'
import { useResolvedDateTimeSettings } from '@/components/date-time/useResolvedDateTimeSettings'
import CmkParagraph from '@/components/typography/CmkParagraph.vue'

import type { ConversationSummary } from '@/nonfree/ai/control-plane/api/conversations'

const props = defineProps<{
  summaries: ConversationSummary[]
  activeId?: string | undefined
}>()

const emit = defineEmits<{
  open: [conversationId: string]
  delete: [conversationId: string]
  back: []
}>()

const { _t } = usei18n()
const dateTimeSettings = useResolvedDateTimeSettings({ dateFormat: 'iso' })

const focusedIndex = ref(0)
const pendingDelete = ref<ConversationSummary | null>(null)
const root = useTemplateRef('root')
const list = useTemplateRef('list')

function focusRow(index: number): void {
  if (props.summaries.length === 0) {
    root.value?.focus()
    return
  }
  const rows = list.value?.querySelectorAll<HTMLButtonElement>('.ai-chat-history__open')
  rows?.[Math.min(Math.max(index, 0), props.summaries.length - 1)]?.focus()
}

function focusCurrentChat(): void {
  const active = props.summaries.findIndex((s) => s.conversation_id === props.activeId)
  if (active === -1 && props.activeId !== undefined) {
    root.value?.focus()
  } else {
    focusRow(Math.max(active, 0))
  }
}

function deleteAt(index: number): void {
  const summary = props.summaries[index]
  if (summary) {
    focusedIndex.value = index
    pendingDelete.value = summary
  }
}

async function confirmDelete(): Promise<void> {
  if (pendingDelete.value) {
    emit('delete', pendingDelete.value.conversation_id)
  }
  await cancelDelete()
}

async function cancelDelete(): Promise<void> {
  pendingDelete.value = null
  await nextTick()
  focusRow(focusedIndex.value)
}

function onKeydown(event: KeyboardEvent, index: number): void {
  if (event.key === 'Delete') {
    event.preventDefault()
    deleteAt(index)
  }
}

function onEscape(event: KeyboardEvent): void {
  event.preventDefault()
  event.stopPropagation()
  emit('back')
}

watch(
  () => props.summaries,
  async () => {
    const hadFocus = root.value?.contains(document.activeElement) ?? false
    await nextTick()
    if (document.activeElement === root.value) {
      focusCurrentChat()
    } else if (hadFocus && !root.value?.contains(document.activeElement)) {
      focusRow(focusedIndex.value)
    }
  }
)

onMounted(focusCurrentChat)
</script>

<template>
  <div ref="root" class="ai-chat-history" tabindex="-1" @keydown.escape="onEscape">
    <CmkParagraph v-if="summaries.length === 0">{{ _t('No chats yet') }}</CmkParagraph>
    <ul v-else ref="list" class="ai-chat-history__list" :aria-label="_t('Chat history')">
      <li
        v-for="(summary, index) in summaries"
        :key="summary.conversation_id"
        class="ai-chat-history__row"
        :class="{ 'ai-chat-history__row--active': summary.conversation_id === activeId }"
      >
        <button
          type="button"
          class="ai-chat-history__open"
          :aria-current="summary.conversation_id === activeId ? 'true' : undefined"
          aria-keyshortcuts="Delete"
          @click="emit('open', summary.conversation_id)"
          @keydown="onKeydown($event, index)"
        >
          {{ summary.title ?? _t('Untitled chat') }}
        </button>
        <time class="ai-chat-history__date" :datetime="summary.updated_at">
          {{ formatDateOrRelativeDay(new Date(summary.updated_at), dateTimeSettings) }}
        </time>
        <CmkButton
          class="ai-chat-history__delete"
          variant="optional"
          size="iconOnly"
          tabindex="-1"
          :title="_t('Delete chat')"
          :aria-label="_t('Delete chat')"
          @click="deleteAt(index)"
        >
          <CmkIcon name="delete" size="small" />
        </CmkButton>
      </li>
    </ul>
    <CmkPopupDialog
      :open="pendingDelete !== null"
      :title="
        _t('Delete chat “%{title}”?', {
          title: pendingDelete?.title ?? _t('Untitled chat')
        })
      "
      @close="cancelDelete"
    >
      <div class="ai-chat-history__confirm">
        <CmkParagraph>
          {{
            _t(
              'This will permanently delete this chat and its messages. This action can’t be undone.'
            )
          }}
        </CmkParagraph>
        <div class="ai-chat-history__confirm-buttons">
          <CmkButton variant="primary" @click="confirmDelete">{{ _t('Delete chat') }}</CmkButton>
          <CmkButton @click="cancelDelete">{{ _t('Cancel') }}</CmkButton>
        </div>
      </div>
    </CmkPopupDialog>
  </div>
</template>

<style scoped>
.ai-chat-history__list {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  overflow: hidden;
  border-radius: var(--border-radius);
  list-style-type: none;
}

.ai-chat-history__row {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--dimension-6);
  padding-right: var(--dimension-5);
  background-color: var(--ux-theme-3);

  & + & {
    border-top: 1px solid var(--ux-theme-1);
  }

  &:hover {
    background-image: linear-gradient(var(--row-hover-bg-color), var(--row-hover-bg-color));
  }

  &:has(.ai-chat-history__open:focus-visible) {
    outline: revert;
    outline-offset: -2px;
  }
}

.ai-chat-history__row--active {
  background-color: var(--ux-theme-5);
}

.ai-chat-history__open {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: var(--dimension-5) 0 var(--dimension-5) var(--dimension-5);
  overflow: hidden;
  border: none;
  background: none;
  color: inherit;
  font-weight: var(--font-weight-bold);
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }

  &:focus-visible {
    outline: none;
  }
}

.ai-chat-history__date {
  flex-shrink: 0;
  color: var(--font-color-secondary);
  white-space: nowrap;
}

.ai-chat-history__delete {
  position: relative;
  z-index: 1;
}

.ai-chat-history__confirm {
  display: flex;
  flex-direction: column;
  gap: var(--dimension-8);
  align-self: stretch;
}

.ai-chat-history__confirm-buttons {
  display: flex;
  gap: var(--dimension-4);
}
</style>
